import React, { useState, useEffect } from 'react';
import { useViewConfig } from '../hooks/useViewConfig';
import '../styles/announcementBar.css';

const DEFAULTS = {
  rotationInterval: 4000,
  showOnMobile: true,
  showOnDesktop: true,
  announcements: [],
};

const AnnouncementBar = () => {
  const { data: config, loading, error } = useViewConfig('ANNOUNCEMENT_BAR');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  // Derive active announcements from config (sorted by array order, active only)
  const cfg = config?.data || {};
  const enabled          = cfg.enabled          ?? true;
  const rotationInterval = cfg.rotationInterval  ?? DEFAULTS.rotationInterval;
  const showOnMobile     = cfg.showOnMobile      ?? DEFAULTS.showOnMobile;
  const showOnDesktop    = cfg.showOnDesktop     ?? DEFAULTS.showOnDesktop;
  const activeItems      = (cfg.announcements    ?? DEFAULTS.announcements)
    .filter(a => a.active !== false);

  // Sync --announcement-bar-height with actual render state
  // Must depend on all the same conditions as the early-return guard below
  useEffect(() => {
    const shouldShow = !loading && !error && !!config && enabled && activeItems.length > 0;
    document.documentElement.style.setProperty(
      '--announcement-bar-height',
      shouldShow ? '40px' : '0px'
    );
    return () => {
      // Always clean up on unmount
      document.documentElement.style.setProperty('--announcement-bar-height', '0px');
    };
  }, [loading, error, config, enabled, activeItems.length]);

  // Reset index when config changes
  useEffect(() => {
    setCurrentIndex(0);
    setVisible(true);
  }, [config]);

  // Rotation timer
  useEffect(() => {
    if (!enabled || activeItems.length <= 1) return;
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setCurrentIndex(prev => (prev + 1) % activeItems.length);
        setVisible(true);
      }, 350);
    }, rotationInterval);
    return () => clearInterval(interval);
  }, [enabled, activeItems.length, rotationInterval]);

  // Guard: render nothing in these cases
  if (loading || error || !config || !enabled || activeItems.length === 0) {
    return null;
  }

  const barClasses = [
    'announcement-bar',
    !showOnMobile  && 'announcement-bar--hide-mobile',
    !showOnDesktop && 'announcement-bar--hide-desktop',
  ].filter(Boolean).join(' ');

  return (
    <div className={barClasses} aria-live="polite" aria-atomic="true">
      <p className={`announcement-text ${visible ? 'announcement-visible' : 'announcement-hidden'}`}>
        {activeItems[currentIndex]?.text}
      </p>
    </div>
  );
};

export default AnnouncementBar;

